import { requisicaoAutenticada } from './http';

/**
 * O que a empresa escolhida faz em cada serviço — o menu e as telas de quem
 * não é admin (`/api/participacao`, `/api/solicitacoes-de-fechamento`).
 *
 * Espelha `apps/api/src/modules/participacao` e `.../solicitacoes`.
 */

/** O papel num serviço. No Pontual, CLIENTE é o comprador. */
export type PapelNoServico = 'CLIENTE' | 'FORNECEDOR' | 'ASSESSOR';

export interface Participacao {
  readonly recorrente: readonly PapelNoServico[];
  readonly pontual: readonly PapelNoServico[];
  readonly passivo: readonly PapelNoServico[];
}

/**
 * A empresa assessora clientes em algum serviço? Quem assessora simula para os
 * clientes em qualquer divisão — inclusive na que ainda não tem contrato.
 */
export function assessoraEmAlgum(participacao: Participacao | undefined): boolean {
  if (participacao === undefined) return false;
  return [...participacao.recorrente, ...participacao.pontual, ...participacao.passivo].includes(
    'ASSESSOR',
  );
}

export interface ClienteDaCarteira {
  readonly businessId: string;
  readonly razaoSocial: string;
  readonly cnpj: string;
}

/** A relação da empresa escolhida com um cliente — o que a tela dele oferece. */
export type RelacaoComCliente = 'ADMIN' | 'CLIENTE' | 'ASSESSOR' | 'FORNECEDOR';

export interface AcessoAoCliente {
  readonly relacao: RelacaoComCliente;
  /** Altera cadastro e cálculo: o gestor do cliente, ou o do assessor dele. */
  readonly podeGerenciar: boolean;
  /** Desativar, liberar serviço, nomear gestor ou signatário. Nunca o assessor. */
  readonly podeGerenciarDiretamente: boolean;
}

/** O admin não pergunta: vê e opera tudo. */
export const ACESSO_DA_OPERACAO: AcessoAoCliente = {
  relacao: 'ADMIN',
  podeGerenciar: true,
  podeGerenciarDiretamente: true,
};

export const carregarAcessoAoCliente = (
  empresa: string,
  clienteId: string,
): Promise<AcessoAoCliente> =>
  requisicaoAutenticada<AcessoAoCliente>(
    `/participacao/clientes/${encodeURIComponent(clienteId)}?empresa=${encodeURIComponent(empresa)}`,
  );

export const carregarParticipacao = (empresa: string): Promise<Participacao> =>
  requisicaoAutenticada<Participacao>(`/participacao?empresa=${encodeURIComponent(empresa)}`);

export const carregarCarteira = (empresa: string): Promise<ClienteDaCarteira[]> =>
  requisicaoAutenticada<ClienteDaCarteira[]>(
    `/participacao/clientes?empresa=${encodeURIComponent(empresa)}`,
  );

// ─── Pedidos de fechamento ───────────────────────────────────────────────────

export type ServicoComPedido = 'PONTUAL' | 'PASSIVO';
export type SituacaoDoPedido = 'ABERTA' | 'ATENDIDA' | 'CANCELADA';

export interface PedidoDeFechamento {
  readonly id: string;
  readonly servico: ServicoComPedido;
  readonly status: SituacaoDoPedido;
  readonly clienteId: string;
  readonly clienteNome: string | null;
  readonly assessorId: string;
  readonly assessorNome: string | null;
  readonly solicitadoPorNome: string | null;
  readonly negociacaoPontualId: string | null;
  readonly contratoPassivoId: string | null;
  /** O que o assessor simulou: `{ entradas, resultado }`, como ele viu. */
  readonly simulacao: Record<string, unknown> | null;
  readonly observacao: string | null;
  readonly criadaEm: string;
  readonly resolvidaEm: string | null;
}

export const ROTULO_DA_SITUACAO_DO_PEDIDO: Readonly<Record<SituacaoDoPedido, string>> = {
  ABERTA: 'Aguardando a operação',
  ATENDIDA: 'Atendido',
  CANCELADA: 'Cancelado',
};

export const CHAVE_DOS_PEDIDOS = ['pedidos-de-fechamento'] as const;

export function listarPedidos(filtros: {
  readonly servico: ServicoComPedido;
  readonly empresa?: string;
  readonly status?: SituacaoDoPedido;
}): Promise<PedidoDeFechamento[]> {
  const query = new URLSearchParams({ servico: filtros.servico });
  if (filtros.empresa !== undefined) query.set('empresa', filtros.empresa);
  if (filtros.status !== undefined) query.set('status', filtros.status);
  return requisicaoAutenticada<PedidoDeFechamento[]>(
    `/solicitacoes-de-fechamento?${query.toString()}`,
  );
}

export const pedirFechamento = (dados: {
  readonly empresa: string;
  readonly servico: ServicoComPedido;
  readonly clienteId: string;
  readonly negociacaoPontualId?: string;
  readonly contratoPassivoId?: string;
  readonly simulacao?: Record<string, unknown>;
  readonly observacao?: string;
}): Promise<PedidoDeFechamento> =>
  requisicaoAutenticada<PedidoDeFechamento>('/solicitacoes-de-fechamento', {
    metodo: 'POST',
    corpo: dados,
  });

export const atenderPedido = (id: string): Promise<PedidoDeFechamento> =>
  requisicaoAutenticada<PedidoDeFechamento>(`/solicitacoes-de-fechamento/${id}/atendimento`, {
    metodo: 'POST',
  });

export const cancelarPedido = (id: string): Promise<PedidoDeFechamento> =>
  requisicaoAutenticada<PedidoDeFechamento>(`/solicitacoes-de-fechamento/${id}/cancelamento`, {
    metodo: 'POST',
  });
